import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Plus, Loader2, X, Trash2, Edit3, ExternalLink, EyeOff, Eye, RefreshCw,
  CheckCircle2, Archive, AlertTriangle, Filter, ShieldCheck, ShieldAlert,
} from "lucide-react";
import { PageHeader, StatusBadge, fmtDate, Empty } from "../../components/admin/ui";

const STATUSES = ["imported", "processing", "ready_for_review", "approved", "published", "archived", "private", "error"];
const VISIBILITIES = ["public", "members_only", "private", "admin_only"];
const SERVICE_TYPES = [
  "Sunday Revival Service", "Midnight Fire Prayer", "Teaching", "Prophetic Night",
  "Fire Hub Gathering", "Deliverance Service", "Healing Service", "Tent Revival",
  "Crusade", "Special Gathering", "Private / Do Not Publish",
];

const empty = {
  title: "", service_type: "Sunday Revival Service", speaker: "Prophet Dishawn S. Gordon",
  date: "", service_date: "", description: "", scripture: "", link: "",
  is_published: false, status: "ready_for_review", visibility: "public",
};

const MediaLibrary = () => {
  const [items, setItems] = useState([]);
  const [overview, setOverview] = useState(null);
  const [sync, setSync] = useState(null);
  const [filter, setFilter] = useState({ status: "", q: "" });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [autoSyncHint, setAutoSyncHint] = useState(null);

  const STALE_DAYS = 7;
  const AUTO_SYNC_FLAG = "rfmg_auto_sync_fired";

  const load = async () => {
    setLoading(true);
    try {
      const [list, ov, sy] = await Promise.all([
        api.get("/admin/recordings"),
        api.get("/admin/media/overview"),
        api.get("/admin/media/sync/status"),
      ]);
      setItems(list.data.items || []);
      setOverview(ov.data);
      setSync(sy.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Auto-trigger Sync Now once per session if Drive is configured AND last sync was >7d ago (or never)
  useEffect(() => {
    if (!sync || !sync.configured || syncing) return;
    if (sessionStorage.getItem(AUTO_SYNC_FLAG) === "1") return;

    const last = sync.last_sync;
    const lastWhen = last?.finished_at || last?.started_at || null;
    const lastStatus = (last?.status || "").toLowerCase();
    const ageDays = lastWhen ? (Date.now() - new Date(lastWhen).getTime()) / 86400000 : Infinity;
    const stale = !last || lastStatus === "error" || ageDays > STALE_DAYS;

    if (!stale) return;
    sessionStorage.setItem(AUTO_SYNC_FLAG, "1");
    const reason = !last ? "no prior sync" : lastStatus === "error" ? "last run failed" : `last sync ${Math.round(ageDays)} days ago`;
    setAutoSyncHint(`Auto-syncing Drive (${reason})…`);
    (async () => {
      setSyncing(true);
      try {
        const r = await api.post("/admin/media/sync/run");
        if (r.data?.status === "success" || r.data?.status === "ok") {
          toast.success("Drive auto-synced — recordings refreshed.");
        } else if (r.data?.status === "not_configured") {
          toast.error("Auto-sync skipped: Drive not configured.");
        }
        await load();
      } catch {
        toast.error("Auto-sync failed — try Sync Now manually.");
      } finally {
        setSyncing(false);
        setAutoSyncHint(null);
      }
    })();
  }, [sync, syncing]);

  const open = (rec) => setEditing(rec ? { ...rec } : { ...empty, date: new Date().toISOString().slice(0, 10), service_date: new Date().toISOString().slice(0, 10) });

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...editing };
    payload.date = payload.service_date || payload.date;
    delete payload.created_at; delete payload.updated_at; delete payload.id;
    delete payload.created_by; delete payload.updated_by; delete payload.published_at; delete payload.archived_at;
    if (!payload.title || !payload.link || !payload.date || !payload.description || !payload.speaker || !payload.service_type) {
      toast.error("Please fill title, type, speaker, date, description, and Drive link."); return;
    }
    payload.is_published = payload.status === "published";
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/recordings/${editing.id}`, payload);
      else await api.post("/admin/recordings", payload);
      toast.success("Recording saved.");
      setEditing(null); load();
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this recording metadata? (The Drive file itself is never touched.)")) return;
    try { await api.delete(`/admin/recordings/${id}`); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  const doPublish = async (id) => {
    try { await api.post(`/admin/media/${id}/publish`); toast.success("Published. Older entries beyond the 5-cap moved to archive."); load(); }
    catch { toast.error("Publish failed."); }
  };
  const doArchive = async (id) => {
    try { await api.post(`/admin/media/${id}/archive`); toast.success("Archived."); load(); }
    catch { toast.error("Archive failed."); }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/media/sync/run");
      if (r.data.status === "not_configured") {
        toast.error("Google Drive sync is not configured yet. Add credentials in Settings.");
      } else if (r.data.status === "pending_implementation") {
        toast("Drive credentials accepted. Live sync activates in Phase 2.");
      } else {
        toast.success("Sync complete.");
      }
      load();
    } catch { toast.error("Sync failed."); }
    finally { setSyncing(false); }
  };

  const filtered = items.filter((r) => {
    if (filter.status && r.status !== filter.status) return false;
    if (filter.q) {
      const q = filter.q.toLowerCase();
      if (!(r.title || "").toLowerCase().includes(q) && !(r.speaker || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-8" data-testid="admin-media-library">
      <PageHeader
        overline="Media Library"
        title={<>The <span className="italic gold-text">archive</span> of the altar</>}
        subtitle="Manual entry works today. Google Drive auto-sync activates once credentials are added in Settings."
      >
        <button onClick={runSync} disabled={syncing} className="btn-ghost !py-2.5 !px-4 !text-sm" data-testid="sync-now-btn">
          {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Sync Now
        </button>
        <button onClick={() => open(null)} className="btn-fire" data-testid="add-recording-btn"><Plus className="h-4 w-4" /> Add Recording</button>
      </PageHeader>

      {/* Sync Health badge — always rendered, useful post-deploy */}
      {sync && <SyncHealthBadge sync={sync} autoSyncHint={autoSyncHint} syncing={syncing} />}

      {/* Drive status banner */}
      {sync && !sync.configured && (
        <div className="bento-card !p-5 border-[#F28C18]/40 flex items-start gap-3" data-testid="drive-not-configured">
          <AlertTriangle className="h-5 w-5 text-[#F28C18] mt-0.5 flex-shrink-0" />
          <div className="text-sm text-[#F6F0E6]">
            <div className="font-medium">Google Drive sync is not configured yet.</div>
            <div className="text-[#D9D9D9]/80 mt-1">
              Missing: {sync.missing_env.join(", ") || "credentials"}. Add them to backend <code>.env</code> and restart the backend to activate automatic sermon import from <code className="text-[#C9A44C]">revivalfireteam@gmail.com</code>.
            </div>
          </div>
        </div>
      )}

      {/* Overview tiles */}
      {overview && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Tile label="Ready for Review" value={overview.counts.ready_for_review} accent="#F28C18" />
          <Tile label="Published Latest" value={overview.latest_displayed} sub={`Cap: ${overview.latest_target}`} />
          <Tile label="Archived" value={overview.counts.archived} />
          <Tile label="Errors" value={overview.counts.error} accent={overview.counts.error ? "#E8452C" : "#C9A44C"} />
        </section>
      )}

      {/* Filters */}
      <section className="bento-card !p-5">
        <span className="glow-blob" />
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-[#C9A44C] tracking-[0.18em] uppercase"><Filter className="h-3.5 w-3.5" /> Filter</div>
          <select className="bg-[#0a0a0a] border border-[#C9A44C]/25 rounded-full px-3 py-1.5 text-xs text-[#F6F0E6] focus:outline-none" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="media-filter-status">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <input className="bg-[#0a0a0a] border border-[#C9A44C]/25 rounded-full px-3 py-1.5 text-xs text-[#F6F0E6] focus:outline-none flex-1 min-w-[180px]" placeholder="Search title or speaker" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} data-testid="media-filter-q" />
        </div>
      </section>

      {/* List */}
      {loading ? (
        <div className="text-[#D9D9D9]/60"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <Empty title="No recordings match" subtitle="Add your first sermon below, or wait for Google Drive sync (once configured) to import meetings automatically." />
      ) : (
        <div className="bento-card !p-0 overflow-hidden" data-testid="media-list">
          <span className="glow-blob" />
          <ul className="divide-y divide-[#C9A44C]/10">
            {filtered.map((r, idx) => (
              <li key={r.id || `media-${idx}`} className="px-5 py-4 flex items-center gap-4 hover:bg-[#C9A44C]/5" data-testid={`media-row-${r.id || idx}`}>
                <div className="flex flex-col items-center justify-center w-14 h-14 rounded-lg border border-[#C9A44C]/20 bg-[#0a0a0a] shrink-0">
                  <div className="text-[9px] tracking-[0.18em] uppercase text-[#C9A44C]">{new Date(r.service_date || r.date || r.created_at).toLocaleString(undefined, { month: "short" })}</div>
                  <div className="font-serif-display text-2xl text-[#F6F0E6] leading-none">{new Date(r.service_date || r.date || r.created_at).getDate() || "—"}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-[#C9A44C]/80">{r.service_type}{r.is_latest && <span className="text-[#C9A44C]">· ★</span>}</div>
                  <h3 className="font-serif-display text-lg text-[#F6F0E6] truncate">{r.title}</h3>
                  <div className="text-[11px] text-[#D9D9D9]/65 truncate">{r.speaker}</div>
                </div>
                <div className="hidden md:block"><StatusBadge status={r.status || (r.is_published ? "published" : "ready_for_review")} /></div>
                <div className="flex items-center gap-1 shrink-0">
                  <a href={r.link} target="_blank" rel="noopener noreferrer" title="Open in Drive" className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-[#C9A44C]/25 hover:border-[#C9A44C]/55"><ExternalLink className="h-3.5 w-3.5 text-[#C9A44C]" /></a>
                  <button onClick={() => open(r)} title="Edit" className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-[#C9A44C]/25 hover:border-[#C9A44C]/55" data-testid={`edit-${r.id}`}><Edit3 className="h-3.5 w-3.5 text-[#D9D9D9]" /></button>
                  {(r.status !== "published" || !r.is_latest) && (
                    <button onClick={() => doPublish(r.id)} title="Publish & Mark Latest" className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-[#C9A44C]/55 hover:bg-[#C9A44C]/10" data-testid={`publish-${r.id}`}><CheckCircle2 className="h-3.5 w-3.5 text-[#C9A44C]" /></button>
                  )}
                  {r.status !== "archived" && (
                    <button onClick={() => doArchive(r.id)} title="Archive" className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-[#C9A44C]/25 hover:border-[#C9A44C]/55" data-testid={`archive-${r.id}`}><Archive className="h-3.5 w-3.5 text-[#D9D9D9]" /></button>
                  )}
                  <button onClick={() => del(r.id)} title="Delete metadata" className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-[#E8452C]/30 text-[#E8452C] hover:bg-[#E8452C]/10" data-testid={`delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-[#050505]/85 backdrop-blur-md flex items-center justify-center px-6 py-10 overflow-y-auto" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bento-card !p-8 max-w-2xl w-full space-y-5" data-testid="recording-form">
            <span className="glow-blob" />
            <div className="flex items-center justify-between">
              <div><div className="overline">{editing.id ? "Edit Recording" : "New Recording"}</div><h2 className="font-serif-display text-2xl text-[#F6F0E6] mt-1">Drive-hosted sermon</h2></div>
              <button type="button" onClick={() => setEditing(null)} className="h-9 w-9 rounded-full border border-[#C9A44C]/30 inline-flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            <div><label className="field-label">Title</label>
              <input className="input-fire" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="rec-title" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="field-label">Service Type</label>
                <select className="input-fire" value={editing.service_type} onChange={(e) => setEditing({ ...editing, service_type: e.target.value })} data-testid="rec-service-type">
                  {SERVICE_TYPES.map((s) => <option key={s} value={s} style={{ background: "#111" }}>{s}</option>)}
                </select>
              </div>
              <div><label className="field-label">Service Date</label>
                <input type="date" className="input-fire" value={editing.service_date || editing.date || ""} onChange={(e) => setEditing({ ...editing, service_date: e.target.value, date: e.target.value })} data-testid="rec-date" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="field-label">Speaker</label>
                <input className="input-fire" value={editing.speaker || ""} onChange={(e) => setEditing({ ...editing, speaker: e.target.value })} data-testid="rec-speaker" /></div>
              <div><label className="field-label">Scripture</label>
                <input className="input-fire" value={editing.scripture || ""} onChange={(e) => setEditing({ ...editing, scripture: e.target.value })} placeholder="e.g. Romans 12:1" data-testid="rec-scripture" /></div>
            </div>
            <div><label className="field-label">Description</label>
              <textarea rows={3} className="input-fire" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="rec-description" /></div>
            <div><label className="field-label">Google Drive Share Link</label>
              <input className="input-fire" placeholder="https://drive.google.com/file/d/…/view?usp=sharing" value={editing.link || ""} onChange={(e) => setEditing({ ...editing, link: e.target.value })} data-testid="rec-link" />
              <p className="mt-1.5 text-[11px] text-[#D9D9D9]/55">Set the file&rsquo;s sharing to &ldquo;Anyone with the link · Viewer&rdquo; for in-page playback.</p></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="field-label">Status</label>
                <select className="input-fire" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="rec-status">
                  {STATUSES.map((s) => <option key={s} value={s} style={{ background: "#111" }}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Visibility</label>
                <select className="input-fire" value={editing.visibility} onChange={(e) => setEditing({ ...editing, visibility: e.target.value })} data-testid="rec-visibility">
                  {VISIBILITIES.map((s) => <option key={s} value={s} style={{ background: "#111" }}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </div>
            </div>
            <button type="submit" disabled={saving} className="btn-fire w-full" data-testid="rec-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {saving ? "Saving…" : editing.id ? "Save changes" : "Add Recording"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

const Tile = ({ label, value, sub, accent = "#C9A44C" }) => (
  <div className="bento-card !p-5">
    <span className="glow-blob" />
    <div className="overline" style={{ color: accent }}>{label}</div>
    <div className="font-serif-display text-3xl text-[#F6F0E6] mt-2">{value}</div>
    {sub && <div className="text-[11px] text-[#D9D9D9]/60 mt-0.5">{sub}</div>}
  </div>
);

const SyncHealthBadge = ({ sync, autoSyncHint, syncing }) => {
  const last = sync.last_sync || null;
  const lastStatus = (last?.status || "").toLowerCase();
  const lastWhen = last?.finished_at || last?.started_at || null;

  // Auto-sync running takes visual precedence
  if (autoSyncHint || syncing) {
    const tone = { border: "#F28C18", bg: "rgba(242,140,24,0.10)", text: "#F28C18" };
    return (
      <div
        className="bento-card !p-4 flex items-start gap-3"
        style={{ borderColor: tone.border + "66", background: tone.bg }}
        data-testid="sync-health-badge"
      >
        <Loader2 className="h-5 w-5 mt-0.5 flex-shrink-0 animate-spin" style={{ color: tone.text }} />
        <div className="text-sm text-[#F6F0E6] min-w-0">
          <div className="font-medium tracking-wide" style={{ color: tone.text }} data-testid="sync-health-headline">
            Drive Sync Health: SYNCING…
          </div>
          <div className="text-[#D9D9D9]/85 mt-1 text-[12.5px]">
            {autoSyncHint || "Pulling the latest sermons from Drive — this usually takes 5–15 seconds."}
          </div>
        </div>
      </div>
    );
  }

  let tone, Icon, headline, detail;
  if (!sync.configured) {
    tone = { border: "#E8452C", bg: "rgba(232,69,44,0.08)", text: "#E8452C" };
    Icon = ShieldAlert;
    headline = "Drive Sync Health: AT RISK";
    detail = `Missing env: ${sync.missing_env.join(", ") || "credentials"}. Recordings will disappear on next deploy until added to Emergent Deploy → Environment Variables.`;
  } else if (lastStatus === "error" || lastStatus === "not_configured") {
    tone = { border: "#E8452C", bg: "rgba(232,69,44,0.08)", text: "#E8452C" };
    Icon = ShieldAlert;
    headline = "Drive Sync Health: LAST RUN FAILED";
    detail = `Last attempt: ${lastStatus}${lastWhen ? ` · ${fmtDate(lastWhen)}` : ""}. Click "Sync Now" to retry.`;
  } else if (!last) {
    tone = { border: "#F28C18", bg: "rgba(242,140,24,0.08)", text: "#F28C18" };
    Icon = ShieldCheck;
    headline = "Drive Sync Health: CONFIGURED · NO SYNC YET";
    detail = `Source: ${sync.source_account}. Click "Sync Now" to import existing sermons.`;
  } else {
    tone = { border: "#C9A44C", bg: "rgba(201,164,76,0.06)", text: "#C9A44C" };
    Icon = ShieldCheck;
    headline = "Drive Sync Health: ALL GREEN";
    detail = `Source: ${sync.source_account}${lastWhen ? ` · Last sync ${fmtDate(lastWhen)}` : ""}${typeof last?.files_imported === "number" ? ` · ${last.files_imported} imported, ${last.files_updated || 0} updated` : ""}.`;
  }

  return (
    <div
      className="bento-card !p-4 flex items-start gap-3"
      style={{ borderColor: tone.border + "66", background: tone.bg }}
      data-testid="sync-health-badge"
    >
      <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: tone.text }} />
      <div className="text-sm text-[#F6F0E6] min-w-0">
        <div className="font-medium tracking-wide" style={{ color: tone.text }} data-testid="sync-health-headline">
          {headline}
        </div>
        <div className="text-[#D9D9D9]/85 mt-1 text-[12.5px]">{detail}</div>
      </div>
    </div>
  );
};

export default MediaLibrary;
